<template>
    <!-- Signature Container with Dynamic Positioning -->
    <div class="signature-template" :style="containerStyle">

        <img 
            :src="sig.signature_image_base64 || placeholderImage" 
            :alt="`Signature of ${sig.official_name}`"
            class="signature-image absolute-image"
            :style="imageStyle"
            @error="handleImageError"
        />

        <!-- Signature Text (free-floating layer) -->
        <div class="signature-text absolute-text">
            <div class="official-name" :style="nameStyle">
                {{ sig.official_name }}
                <span v-if="sig.official_title">, {{ sig.official_title }}</span>
            </div>

            <div class="position-title" :style="positionStyle">
                {{ sig.position_title }}
            </div>
        </div>

        <!-- Signature Line -->
        <!-- <div v-if="showLine" class="signature-line" :style="lineStyle"></div> -->
    </div>
</template>

<script setup>
import { computed, ref } from 'vue';
/* eslint-disable */
const props = defineProps({
    sig: {
        type: Object,
        default: () => ({})
    },
    // Positioning (from signature_positions JSON)
    position: {
        type: Object,
        default: () => ({
            x: 75,    // percentage from left
            y: 85,    // percentage from top
            width: 20, // percentage width
            height: 8  // percentage height
        })
    },
    maxImgHeight: {
        type: String,
        default: "60px"
    },
    // Styling options
    fontSize: {
        type: String,
        default: "14px"
    },
    sigMarginTop: {
        type: String,
        default: "0px"
    },
    fontFamily: {
        type: String,
        default: "'Times New Roman', Times, serif"
    },
    textColor: {
        type: String,
        default: "#000000"
    },
    lineColor: {
        type: String,
        default: "#000000"
    },
    lineWidth: {
        type: String,
        default: "1px"
    },
    // Display options
    showLine: {
        type: Boolean,
        default: false
    },
    showDate: {
        type: Boolean,
        default: false
    },
    // Container options
    containerType: {
        type: String,
        default: "absolute" // 'absolute', 'relative', 'inline'
    },
    // For absolute positioning within parent document
    parentWidth: {
        type: Number,
        default: 2480 // Default A4 width in pixels
    },
    parentHeight: {
        type: Number,
        default: 3508 // Default A4 height in pixels
    },
    imageOffsetY:{
        type: Number,
        default: -40
    }
});

// State
const imageError = ref(false);
const placeholderImage = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjgwIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9IiNmNWY1ZjUiLz48dGV4dCB4PSI1MCUiIHk9IjUwJSIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjE0IiBmaWxsPSIjY2NjIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+Tm8gU2lnbmF0dXJlPC90ZXh0Pjwvc3ZnPg';

// Computed
const currentDate = computed(() => {
    return new Date().toLocaleDateString('en-PH', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    });
});

// Calculate pixel values from percentages
const positionPx = computed(() => {
    const x = (props.position.x / 100) * props.parentWidth;
    const y = (props.position.y / 100) * props.parentHeight;
    const width = (props.position.width / 100) * props.parentWidth;
    const height = (props.position.height / 100) * props.parentHeight;

    return { x, y, width, height };
});

// Dynamic Styles
const containerStyle = computed(() => {
    const baseStyles = {
        fontFamily: props.fontFamily,
        color: props.textColor,
        fontSize: props.fontSize,
        boxSizing: 'border-box'
    };

    // Different positioning modes
    if (props.containerType === 'absolute') {
        return {
            ...baseStyles,
            position: 'absolute',
            left: `${positionPx.value.x}px`,
            top: `${positionPx.value.y}px`,
            width: `${positionPx.value.width}px`,
            minHeight: `${positionPx.value.height}px`,
            zIndex: 1000
        };
    } else if (props.containerType === 'relative') {
        return {
            ...baseStyles,
            position: 'relative',
            width: '100%',
            margin: '0 auto',
            padding: '10px'
        };
    } else {
        // inline
        return {
            ...baseStyles,
            display: 'inline-block',
            verticalAlign: 'top',
            margin: '0px'
        };
    }
});

const imageContainerStyle = computed(() => ({
    width: '100%',
    height: 'auto',
    marginBottom: '0px',
    marginTop: props.sigMarginTop,
    display: 'flex',
    justifyContent: 'center'
}));

const imageStyle = computed(() => ({
    maxWidth: '100%',
    maxHeight: props.maxImgHeight,
    position: 'absolute',
    top: props.imageOffsetY + 'px',   //⭐ move image upward
    left: '0',
    right: '0',
    margin: '0 auto',
    objectFit: 'contain',
    filter: imageError.value ? 'grayscale(100%)' : 'none',
    opacity: imageError.value ? 0.5 : 1,
    zIndex: 2
}));

const detailsStyle = computed(() => ({
    textAlign: 'center',
    lineHeight: '1.2'
}));

const nameStyle = computed(() => {
    // Extract number and unit
    const fontSizeNum = parseFloat(props.fontSize);
    const fontSizeUnit = props.fontSize.replace(/[\d.-]/g, '');

    // Calculate in JavaScript
    const calculatedSize = fontSizeNum * 1.1;

    return {
        fontSize: `${calculatedSize}${fontSizeUnit}`,
        fontWeight: 'bold',
        marginBottom: '2px'
    };
});



const positionStyle = computed(() => {
    // Extract number and unit
    const fontSizeNum = parseFloat(props.fontSize);
    const fontSizeUnit = props.fontSize.replace(/[\d.-]/g, '');

    // Calculate in JavaScript
    const calculatedSize = fontSizeNum * 0.9;

    return {
        fontSize: `${calculatedSize}${fontSizeUnit}`,
        marginBottom: '2px'
    };
});

const departmentStyle = computed(() => ({
    fontSize: `calc(${props.fontSize} * 0.8)`,
    fontStyle: 'italic',
    marginBottom: '2px'
}));

const licenseStyle = computed(() => ({
    fontSize: `calc(${props.fontSize} * 0.8)`,
    marginBottom: '2px'
}));

const dateStyle = computed(() => ({
    fontSize: `calc(${props.fontSize} * 0.8)`,
    marginTop: '5px',
    color: '#666'
}));

const lineStyle = computed(() => ({
    width: '100%',
    height: props.lineWidth,
    backgroundColor: props.lineColor,
    marginTop: '5px'
}));

// Methods
const handleImageError = () => {
    imageError.value = true;
    console.warn('Signature image failed to load for:', props.sig.official_name);
};
</script>

<style scoped>
.signature-template {
    position: absolute;
    pointer-events: none;
    font-family: inherit;
    color: inherit;
}

/* Floating signature image */
.absolute-image {
    position: absolute;
    left: 0;
    right: 0;
    margin: auto;
    display: block;
    z-index: 2;
    /* ABOVE text if needed */
    transform: translateY(0);
}

/* Floating text block */
.absolute-text {
    position: absolute;
    left: 0;
    right: 0;
    margin: auto;
    text-align: center;
    width: 100%;
    z-index: 3;
    /* ABOVE signature if overlapping */
    pointer-events: none;
}


.signature-image {
    transition: all 0.3s ease;
}

.signature-image:hover {
    transform: scale(1.02);
}

.official-name {
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.position-title {
    text-transform: uppercase;
    font-weight: 500;
}

.department {
    color: #555;
}

.signature-line {
    border-top-style: solid;
}

/* Print styles */
@media print {
    .signature-template {
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
    }

    .signature-image {
        filter: none !important;
        opacity: 1 !important;
    }
}
</style>